// @ts-strict-ignore
import {Input, Directive, inject} from '@angular/core';

import { ListResource } from '@resources/list.resource';
import { KeyedResource } from '@resources/keyed.resource';
import { LMLoggerService } from '@services/lm-logger.service';
import { AppConstants } from '@utils/app.constants';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMFormatUtilService } from '@services/utils';
import { Subscription } from 'rxjs';
import { LMConditionUtilService } from '@app/utils';
import { LMActionService, LMContextService } from "@app/services";

@Directive()
export abstract class LMListContentBaseComponent {
  protected lmContextService = inject(LMContextService);
  protected lmActionService = inject(LMActionService);

  @Input() listUrlInfo: LMListUrlInfo;

  /**
   * The listSpec used to build
   * @type {LMListSpec}
   */
  @Input() set listSpec(listSpec: LMListSpec) {
    if (!listSpec) {
      return; // safety
    }

    this.lmLoggerService.log([ this, this.instanceId, 'set listSpec()' ]);
    this._listSpec = listSpec;

    /*
     * set the list resource for use in paging.
     */
    this.listResource = new ListResource({ resourceId: listSpec.resourceId });

    this.inDashboardPanel = listSpec.listType === 'dashboard';

    if (this.listUrlInfo?.sourceResource) {
      /**
       * if there is a "source" resource set the "from" resource for use in paging.
       * todo: doesn't seem like this is needed as sourceResource could be used instead.
       */
      this.fromResource = new KeyedResource({
        resourceId: this.listUrlInfo.sourceResource.resourceType,
        resourceType: this.listUrlInfo.sourceResource.resourceId
      });
    }
  };

  /**
   * Causes the datagrid to reload with the given set of filterCriteria
   */
  @Input() set filterCriteria(filterCriteria: LMFilterFieldCriteria[]) {
    this.lmLoggerService.trace([ this, this.instanceId, 'filterCriteria', filterCriteria ]);

    // check to make sure filterCriteria has changed from undefined to a null or an Array of at least 1 object
    if (this.m_filterCriteria === undefined && (filterCriteria === undefined || filterCriteria === null || filterCriteria.length === 0)) {
      // Then this is first time in and hence it just an initialization. Make sure to set
      // m_filterCriteria to null so 2nd time in with a null will run the filterPage.
      // This is done so that search form being executed w/o filter criteria will show
      // all records.
      this.m_filterCriteria = null;
      return;
    }

    this.m_filterCriteria = filterCriteria;

    // need to wait on the fromDataView to come in before calling filterCriteriaChanged
    if (this.listSpec.dependentList && !this.fromDataView) {
      return;
    }

    this.filterCriteriaChanged();
  }

  get filterCriteria(): LMFilterFieldCriteria[] {
    return this.m_filterCriteria;
  }

  @Input() set columnfilterOperators(filterOperators: Map<string, string>) {
    this.m_columnfilterOperators = filterOperators;
  }

  get columnfilterOperators(): Map<string, string> {
    return this.m_columnfilterOperators;
  }

  /**
   * Causes the datagrid to reload with the given set of filterCriteria
   */
  @Input() set filterListFilterCriteria(filterCriteria: LMFilterFieldCriteria[]) {
    this.lmLoggerService.trace([ this, this.instanceId, 'filterCriteria', filterCriteria ]);
    this.m_filterListFilterCriteria = filterCriteria;

    if (this.m_filterListFilterCriteria === undefined || this.m_filterListFilterCriteria === null) {
      return;
    }

    this.filterListCriteriaChanged();
  }

  get filterListFilterCriteria(): LMFilterFieldCriteria[] {
    return this.m_filterListFilterCriteria;
  }

  /**
   * Used to retrieve data when this list is a 'dependentList'
   */
  @Input() set fromDataView(fromDataView: LMDataView) {
    if (this.listSpec?.dependentList || this.listSpec?.busListSpec.showChangedValuesOnRight) {
      this.lmLoggerService.trace([ this, 'fromDataView' ]);
      if ((<LMListDataView>fromDataView)?.isTreeMoreLink) {
        fromDataView = null;
      }

      // if undefined then this is angular initializing so don't run fromDataViewChanged().
      if (fromDataView === undefined) {
        return;
      }

      const previousFromResource = this._fromDataView;
      this._fromDataView = fromDataView;

      this._exportFilterCriteria = this._fromDataView?.exportFilterCriteria;

      // fromResource keys change we are on a new record so fetch new data
      // expected that  refresh is called if the fromDataView was only updated!
      if (!this.isSameFromResource(fromDataView, previousFromResource)) {
        this.allowDataLoading = false; //CHERYL what is this for!
        this.fromDataViewChanged();
      }
    }
  }

  public unSelectRow(rowToUnselect: number): void {
    // todo - implement me
  }

  private isSameFromResource(fromDataView1: LMDataView, fromDataView2: LMDataView): boolean {
    if (fromDataView1?.cubeInfo && fromDataView2?.cubeInfo) {
      // Resource Ids are just the cube mediator and they are the same for all dataviews.
      const dimTree1 = fromDataView1.cubeInfo.dimensions.map(dim => dim.ddV).join(' ');
      const dimTree2 = fromDataView2.cubeInfo.dimensions.map(dim => dim.ddV).join(' ');
      let retVal = dimTree1 === dimTree2;

      if (fromDataView1.cubeInfo.extraInfo && fromDataView2.cubeInfo.extraInfo) {
        if (fromDataView1.cubeInfo.extraInfo.periodKey) {
          retVal = retVal && (fromDataView1.cubeInfo.extraInfo.periodKey === fromDataView2.cubeInfo.extraInfo.periodKey);
        }
        if (fromDataView1.cubeInfo.extraInfo.periodView) {
          retVal = retVal && (fromDataView1.cubeInfo.extraInfo.periodView === fromDataView2.cubeInfo.extraInfo.periodView)
            && (fromDataView1.cubeInfo.extraInfo.pvPeriodIdx === fromDataView2.cubeInfo.extraInfo.pvPeriodIdx);
        }
      }
      return  retVal;
      // Use dataViewResId which is based on the view key of dataview.
      // return fromDataView1.dataViewResId === fromDataView2.dataViewResId;
    } else {
      // eslint-disable-next-line eqeqeq
      const resourceId = fromDataView1 ? fromDataView1.resourceId : fromDataView1;
      const newResourceId = fromDataView2 ? fromDataView2.resourceId : fromDataView2;
      if (resourceId === newResourceId) {
        if (this.eFCMatch(fromDataView1.exportFilterCriteria, fromDataView2.exportFilterCriteria)) {
          return true;
        } else {
          this.hasEFCChanged = true;
          return false;
        }
      }
      return resourceId === newResourceId;
    }
  }

  // check if the exported filter criteria has changed. If it has, the data needs to be reloaded even if the
  // resourceID on the dataviews match
  private eFCMatch(efc1: Array<LMFilterFieldCriteria>, efc2: Array<LMFilterFieldCriteria>): boolean {
    let efCMatch = true;

    if (efc1 === undefined && efc2 === undefined) {
      return true;
    } else if (efc1 === undefined && efc2 !== undefined) {
      return false;
    } else if (efc1 !== undefined && efc2 === undefined) {
      return false;
    } else if (efc1 !== undefined && efc2 !== undefined) {
      if (efc1.length !== efc2.length) {
        return false;
      } else {
        // lengths match, need to check fieldIDS, values and filter types
        efc1.forEach((fc1: LMFilterFieldCriteria) => {
          efc2.forEach((fc2: LMFilterFieldCriteria) => {
            if (efCMatch) {
              if (fc1.fieldId === fc2.fieldId) {
                if (fc1.value !== fc2.value) {
                  efCMatch = false;
                } else if (fc1.operator !== fc2.operator) {
                  efCMatch = false;
                }
              }
            }
          });
        });
      }
    }

    return efCMatch;
  }

  /**
   * The information needed to fill out data for a select list.
   */
  @Input() selectRequestData: SelectRequestData;

  /**
   * ListState for a list and all it's contents
   */
  @Input() listState: LMListState = {};

  /**
   * ListPersonalization for a list and all it's contents
   */
  @Input() listPersonalization: LMListPersonalization = {};

  @Input() set disabled(disabled: boolean) {
    this._disabled = disabled;
    // disabled ? this.clearEmptyMessage() : this.setNoDataMessage(this.filterCriteria && this.filterCriteria.length > 0)
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @Input() implementsManualLoad: boolean;

  @Input() manualLoadInfo: LMManualLoadInfo;

  protected selectListEditModeChanged = false;

  private _inSelectListEditMode = false;
  @Input() public set inSelectListEditMode(inSelectListEditMode: boolean) {
    this._inSelectListEditMode = inSelectListEditMode;
    this.inSelectedListEditModeChanged();
  }

  public get inSelectListEditMode(): boolean {
    return this._inSelectListEditMode;
  }

  protected _multiSelectedValues: LMMultiSelectedValue[];
  @Input() public set multiSelectedValues(multiSelectedValues: LMMultiSelectedValue[]) {
    this._multiSelectedValues = multiSelectedValues;
    this.multiSelectedValuesChanged();
  }

  public get multiSelectedValues(): LMMultiSelectedValue[] {
    return this._multiSelectedValues;
  }

  /**
   * Internal member variable, used to hold the filter field state.
   * Get set by using the filterCriteria @Input.
   */
  public _listSpec: LMListSpec;

  /**
   * interntal member variable, used by the view to determine if
   * this list is in a dashboard panel.
   */
  public inDashboardPanel: boolean;

  /**
   * Used for lm-page-standalone for each list implementation that uses it.
   */
  public pageSizes: number[] = AppConstants.PAGE_SIZES;

  public dataViewSet: LMDataViewSet;
  public savedSearches: LMDataViewSet;

  /**
   * Internal member variable, used to hold the filter field state.
   * Get set by using the filterCriteria @Input.
   */
  protected m_filterCriteria: LMFilterFieldCriteria[];

  /**
   * Internal member variable, used to hold internal filter field state.
   * Gets set by using the filterListFilterCritera @Input.
   */
  protected m_filterListFilterCriteria: LMFilterFieldCriteria[];

  protected m_columnfilterOperators: Map<string, string>;

  protected allowDataLoading: boolean;

  protected pagingSubscription: Subscription;

  /**
   * Used by get/set fromDataView typescript property methods
   * Set to undefined to distinguish it from a null fromDataView.
   * - undefined fromDataView means dependent list is not yet ready to be used.
   * - null fromDataView is a valid input value meaning that this dependent list should be treated as disabled.
   */
  protected _fromDataView = undefined;
  public hasEFCChanged = false;

  /**
   * Whether or not the list implementation (grid, list-view, tree-list, etc...) should be disabled or not.
   */
  protected _disabled;

  protected listResource: ListResource;
  protected fromResource: KeyedResource;

  private _noListDataMessage: SohoEmptyMessageOptions = null;
  private get noListDataMessage(): SohoEmptyMessageOptions {
    if (!this._noListDataMessage) {
      this._noListDataMessage = {
        title: Soho.Locale.translate('NoDataAvailable'),
        icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-no-data-new' : '',
        color: 'azure'
      };
    }

    return this._noListDataMessage;
  }

  private _noSearchDataMessage: SohoEmptyMessageOptions = null;
  private get noSearchDataMessage(): SohoEmptyMessageOptions {
    if (!this._noSearchDataMessage) {
      this._noSearchDataMessage = {
        title: Soho.Locale.translate('NoSearchResultsFound'),
        icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-no-search-result': '',
        color: 'azure'
      };
    }

    return this._noSearchDataMessage;
  }

  // Search Empty Message displayed to inform the user
  // to do search to display records on the list
  private get useSearchToFindDataMessage(): SohoEmptyMessageOptions {
    return {
      title: Soho.Locale.translate('UseSearchToFindData'),
      icon: 'icon-empty-search-data-new',
      color: 'azure'
    };
  }

  private _noChartableDataAvailable: SohoEmptyMessageOptions = null;
  private get noChartableDataAvailable(): SohoEmptyMessageOptions {
    if (!this._noChartableDataAvailable) {
      this._noChartableDataAvailable = {
        title: Soho.Locale.translate('NoChartableDataAvailable'),
        icon: 'icon-empty-no-analytics-new',
        color: 'azure'
      };
    }

    return this._noChartableDataAvailable;
  }

  private _exportFilterCriteria: Array<LMFilterFieldCriteria>;
  protected get exportFilterCriteria(): Array<LMFilterFieldCriteria> {
    return this._exportFilterCriteria;
  }

  private manualLoadMessage(manualLoadInfo: LMManualLoadInfo): SohoEmptyMessageOptions {
    // default message options
    const messageOptions: SohoEmptyMessageOptions = {
      title: Soho.Locale.translate('ManuallyLoadData'),
      icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-search-data-new': '',
      color: 'azure',
      button: {
        id: 'loadDataBtn',
        text: Soho.Locale.translate('LoadData'),
        isPrimary: true,
        click: (event) => {
          event.stopPropagation();
          this.allowDataLoading = true;
          this.loadManualData();
        }
      }
    };

    if (manualLoadInfo) {
      const dataViewHeader = this.dataViewSet && this.dataViewSet.header ? this.dataViewSet.header : undefined;
      if (manualLoadInfo.message) {
        messageOptions.title = this.lmFormatUtilService.formatMessageRule(manualLoadInfo.message, dataViewHeader);
      }

      if (manualLoadInfo.description) {
        messageOptions.info = this.lmFormatUtilService.formatMessageRule(manualLoadInfo.description, dataViewHeader);
      }

      if (manualLoadInfo.buttonText) {
        messageOptions.button.text = this.lmFormatUtilService.formatMessageRule(manualLoadInfo.buttonText, dataViewHeader);
      }

      if (manualLoadInfo.icon) {
        // must be the full soho empty message icon name
        if (this.lmThemeService.isValidSohoIconEmptyMessage(manualLoadInfo.icon)) {
          messageOptions.icon = manualLoadInfo.icon === 'icon-empty-no-search-result'
            ? manualLoadInfo.icon
            : manualLoadInfo.icon.endsWith('-new') ? manualLoadInfo.icon : manualLoadInfo.icon + '-new';
        }
      }
    }

    return messageOptions;
  }

  private noDataEmptyMessage(noDataEmptyMessageInfo: LMNoDataEmptyMessageInfo): SohoEmptyMessageOptions {
    // default message options
    const messageOptions: SohoEmptyMessageOptions = {
      icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-no-data-new' : '',
      color: 'azure'
    };

    if (this.lmContextService.drillAroundWindow()) {
      return messageOptions;
    }

    if (noDataEmptyMessageInfo) {
      const dataViewHeader: LMDataView = this.dataViewSet && this.dataViewSet.header ? this.dataViewSet.header : undefined;
      messageOptions.title = this.lmFormatUtilService.formatMessageRule(noDataEmptyMessageInfo.message, dataViewHeader);

      if (noDataEmptyMessageInfo.description) {
        messageOptions.info = this.lmFormatUtilService.formatMessageRule(noDataEmptyMessageInfo.description, dataViewHeader);
      }

      if (noDataEmptyMessageInfo.icon) {
        // must be the full soho empty message icon name
        if (this.lmThemeService.isValidSohoIconEmptyMessage(noDataEmptyMessageInfo.icon)) {
          messageOptions.icon = noDataEmptyMessageInfo.icon === 'icon-empty-no-search-result'
            ? noDataEmptyMessageInfo.icon
            : noDataEmptyMessageInfo.icon.endsWith('-new') ? noDataEmptyMessageInfo.icon : noDataEmptyMessageInfo.icon + '-new';
        }
      }

      if (noDataEmptyMessageInfo.button) {
        let isVisible = true;
        let isValid = true;
        const dataView: LMDataView = this.dataViewSet && this.dataViewSet.header ? this.dataViewSet.header : undefined;
        if (noDataEmptyMessageInfo.button.visibility) {
          isVisible = this.lmConditionUtilService.isValid(noDataEmptyMessageInfo.button.visibility, dataView);
        }

        if (noDataEmptyMessageInfo.button.validity) {
          isValid = this.lmConditionUtilService.isValid(noDataEmptyMessageInfo.button.validity, dataView);

        } else {
          if (this.dataViewSet?.header?.actionIndexes && noDataEmptyMessageInfo.button?.actionSpec) {
            isValid = this.lmActionService.isValidAction(
              noDataEmptyMessageInfo.button.actionSpec,
              this.listSpec.busClassSpec.actions,
              this.dataViewSet.header.actionIndexes,
              noDataEmptyMessageInfo.button.actionSpec.type,
              false);
          }
        }

        if (isVisible && isValid) {
          const button: SohoEmptyMessageButtonOptions = {
            id: 'noDataBtn',
            text: this.lmFormatUtilService.formatMessageRule(noDataEmptyMessageInfo.button.title, dataViewHeader),
            isPrimary: true,
            click: (event) => {
              event.stopPropagation();
              const columnDef: ListGridColumn = { displayField: noDataEmptyMessageInfo.button };
              this.onFormButtonClicked(columnDef, dataView, null, null, null, event, true);
            }
          };
          messageOptions.button = button;
        }
      }
    }

    return messageOptions;
  }

  private emptyMessageAttachmentList(attachmentListInfo: LMAttachmentListInfo): SohoEmptyMessageOptions {
    // default message options for attachment lists
    const messageOptions: SohoEmptyMessageOptions = {
      icon: this.listSpec?.listType !== 'multilist' ? 'icon-empty-no-data-new' : '',
      color: 'azure'
    };

    if (attachmentListInfo) {
      const dataViewHeader: LMDataView = this.dataViewSet && this.dataViewSet.header ? this.dataViewSet.header : undefined;
      messageOptions.title = '';
      messageOptions.info = Soho.Locale.translate('SelectFiles'); // adjust message for drag and drop files.

      // Attachment Lists with no data message and icon defined.
      if (this.listSpec.busListSpec?.noDataEmptyMessageInfo?.message) {
        messageOptions.info = this.lmFormatUtilService.formatMessageRule(this.listSpec.busListSpec?.noDataEmptyMessageInfo?.message, dataViewHeader);
      }
      if (this.listSpec.busListSpec?.noDataEmptyMessageInfo?.icon) {
        messageOptions.icon = this.listSpec.busListSpec?.noDataEmptyMessageInfo?.icon;
      }

      if (attachmentListInfo?.button) {
        let isValid = true;
        if (this.dataViewSet?.header?.actionIndexes && attachmentListInfo.button?.actionSpec) {
          isValid = this.lmActionService.isValidAction(
            attachmentListInfo.button.actionSpec,
            this.listSpec.busClassSpec.actions,
            this.dataViewSet.header.actionIndexes,
            attachmentListInfo.button.actionSpec.type,
            false);
        }
        if (isValid) {
          const button: SohoEmptyMessageButtonOptions = {
            id: 'noDataBtn',
            text: this.lmFormatUtilService.formatMessageRule(attachmentListInfo?.actionSpec?.label, dataViewHeader),
            isPrimary: true,
            click: (event) => {
              event.stopPropagation();
              const columnDef: ListGridColumn = { displayField: attachmentListInfo?.button };
              const dataView: LMDataView = this.dataViewSet && this.dataViewSet.header ? this.dataViewSet.header : undefined;
              this.onFormButtonClicked(columnDef, dataView, null, null, null, event, true);
            }
          };
          messageOptions.button = button;
        }
      }
    }
    return messageOptions;
  }

  private static errorLoadingMessage: SohoEmptyMessageOptions = {
    icon: 'icon-empty-error-loading-new',
    color: 'azure'
  };

  /** public so it can be used in a template of any subclasses. */
  public emptyMessage: SohoEmptyMessageOptions = null;

  protected instanceId: string;

  /**
   * If ngOnDestroy is called, set this flag to true. this will be used
   * internally by the component for any extraneous calls still attached
   * to this component.
   */
  private m_componentDestroyed = false;

  protected constructor(
    protected lmLoggerService: LMLoggerService,
    protected lmThemeService: LMThemeService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmConditionUtilService: LMConditionUtilService
  ) {
    this.instanceId = this.lmLoggerService.instanceId;
  }

  protected abstract build(): void;

  protected abstract fromDataViewChanged(): void;

  protected abstract filterCriteriaChanged(): void;

  protected abstract filterListCriteriaChanged(): void;

  protected inSelectedListEditModeChanged(): void {
  }

  public multiSelectedValuesChanged(): void {
  }

  public disableSaveInUserListViewMenu(): boolean {
    return true;
  }

  public clearUserListView(): void {
    this.listState.userListViewItem = undefined;
  }

  protected abstract loadManualData(): void;

  protected abstract onFormButtonClicked(columnDef: ListGridColumn, dataView: LMDataView, row: any, cell: any, navigationParams: any, event: any, fromNoDataMessageFormButton?: boolean): void;

  public isManualLoading(): boolean {
    return !!(this.implementsManualLoad && !this.allowDataLoading);
  }

  get listSpec(): LMListSpec {
    return this._listSpec;
  }

  get fromDataView(): LMDataView {
    return this._fromDataView;
  }

  public getDataViewSet(): LMDataViewSet {
    return this.dataViewSet;
  }

  public getDataView(index: number) {
    if (!this.dataViewSet || this.dataViewSet.data.length <= 0) {
      return null;
    }
    return this.dataViewSet.data[ index ];
  }

  protected setEmptyMessage() {
    if (this.disabled) {
      this.clearEmptyMessage();
      return;
    }

    if (this.isManualLoading()) {
      this.emptyMessage = this.manualLoadMessage(this.manualLoadInfo);
      return;
    }

    if (this.listSpec.busListSpec?.attachmentListInfo?.button) {
      this.emptyMessage = this.emptyMessageAttachmentList(this.listSpec.busListSpec.attachmentListInfo);
      return;
    }

    const searchForm = this.listSpec.busListSpec.searchForm;
    if (searchForm && !searchForm.useSearchAsFilter && !(this.filterCriteria && this.filterCriteria.length > 0)) {
      this.emptyMessage = this.useSearchToFindDataMessage;
      return;
    }

    this.setNoDataMessage(this.filterCriteria && this.filterCriteria.length > 0);

  }

  protected clearEmptyMessage() {
    this.emptyMessage = null;
  }

  protected handleError(message: string, title: string) {
    const error: LMError = { message, title };
    this.setErrorLoadingMessage(error);
  }

  protected setErrorLoadingMessage(error: LMError) {
    this.emptyMessage = Object.assign(
      LMListContentBaseComponent.errorLoadingMessage,
      {
        title: error.title,
        info: error.message
      });
  }

  protected setNoDataMessage(noSearchData: boolean) {
    this.emptyMessage = noSearchData
      ? this.noSearchDataMessage
      : (this.listSpec.busListSpec.noDataEmptyMessageInfo)
        ? this.noDataEmptyMessage(this.listSpec.busListSpec.noDataEmptyMessageInfo)
        : this.noListDataMessage;
  }

  protected setNoChartableDataMessage() {
    this.emptyMessage = this.noChartableDataAvailable;
  }

  public get componentDestroyed(): boolean {
    return this.m_componentDestroyed;
  }

  protected setComponentDestroyed() {
    this.m_componentDestroyed = true;
  }

  protected validateInitialInputs(): void {
    if (!this.listSpec) {
      throw 'component requires listSpec before ngOnInit';
    }
    if (!this.listState) {
      throw 'component requires listState before ngOnInit';
    }
    if (!this.listPersonalization) {
      throw 'component requires listPersonalization before ngOnInit';
    }
  }

  protected _hidePagerBar(): boolean {
    if (this.listSpec.showAsMingleWidget) {
      return true;
    }

    if (this.dataViewSet && this.dataViewSet.data) {

      // If the list has no data, hide the pager bar
      if (this.dataViewSet.data.length === 0) {
        return true;
      }

      if (this.listSpec.listType === 'multilist'
        && !this.dataViewSet.pagingInfo.hasNext
        && !this.dataViewSet.pagingInfo.hasPrevious
        &&  this.dataViewSet.data.length <= this.listSpec.pageSize) {
        // for multi-list listSpec.pageSize is the fixedNumberOfRows defined in the LPL
        // todo: maybe make a fixedNumberOfRows param on the LMListSpec definition
        return true;
      }
    }

    return false;
  }

  protected isBusyPaging(): boolean {
    return this.pagingSubscription && !this.pagingSubscription.closed;
  }

  protected cancelOutstandingPagingRequest(): void {
    if (this.pagingSubscription && !this.pagingSubscription.closed) {
      // ----------------------------------------------------------------
      // If a call to the back end is still out then we want to
      // unsubscribe from it to cancel the client side processing
      // of the http client request.
      // ----------------------------------------------------------------
      this.lmLoggerService.log([ this, this.instanceId, 'cancelOutstandingPagingRequest(): pagingSubscription is still open, unsubscribing...' ]);
      this.pagingSubscription.unsubscribe();
    }
  }


  private fcContainsAllEFC(fc: Array<LMFilterFieldCriteria>, efc: Array<LMFilterFieldCriteria>) {
    const checker = (fc, efc) => efc.every(v => fc.includes(v));
    return checker(fc, efc);
  }

  protected getCombinedFilterCriteria(): Array<LMFilterFieldCriteria> {
    // Since EFC are passed in on main lists, we need to make sure they are also there as FCs
    let filterCriteria = this.addFCFromEFC(this.filterCriteria);
    if (this.listSpec.dependentList) {
      if (this.fromDataView && this.exportFilterCriteria && filterCriteria) {
        if (!this.fcContainsAllEFC(filterCriteria, this.exportFilterCriteria)) {
          filterCriteria.push(...this.exportFilterCriteria);
        }
      } else if (this.fromDataView && this.exportFilterCriteria) {
        filterCriteria = this.exportFilterCriteria;
      }
    }

    return filterCriteria;
  }

  private addFCFromEFC(filterCriteria: Array<LMFilterFieldCriteria>): Array<LMFilterFieldCriteria> {
    if (filterCriteria?.length > 0) {
      const tmp: Array<LMFilterFieldCriteria> = [];
      // Need to make a deep copy or will change the exportFilterCriteria on the parent list
      filterCriteria = JSON.parse(JSON.stringify(this.filterCriteria));
      filterCriteria.forEach((fc: LMFilterFieldCriteria) => {
        if (fc.exportFilterCriteria) {
          // make sure the exported filter criteria are also added as regular filter criteria for main lists
          const tmpFC = JSON.parse(JSON.stringify(fc));
          tmpFC.exportFilterCriteria = false;
          tmp.push(tmpFC);
        }
      });

      filterCriteria = [ ...filterCriteria, ...tmp ];
    }

    return filterCriteria;
  }
}
